/**
 * HospitalAdminPage — `/admin/hospital/:hospitalId`
 *
 * The working panel for a hospital: staff, patients and their records,
 * appointments, pharmacy stock with batches and expiry, and reports.
 * Open to the Hospital Admin (and the platform Super Admin). Clinical notes
 * are hidden from receptionists, lab technicians and support staff — the
 * server decides, the UI just reflects it.
 */
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import hospitalApi, { ROLE_LABELS, TYPE_LABELS, STATUS_TONE } from '@/lib/hospitalApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Stethoscope, Users, CalendarClock, Pill, BarChart3, Plus, Lock,
  AlertTriangle, Mail, UserPlus, Trash2,
} from 'lucide-react';

const TABS = [
  ['overview', 'Overview', BarChart3],
  ['staff', 'Staff', Users],
  ['patients', 'Patients', Stethoscope],
  ['appointments', 'Appointments', CalendarClock],
  ['pharmacy', 'Pharmacy', Pill],
  ['enquiries', 'Enquiries', Mail],
];

const Stat = ({ label, value, tone = 'text-slate-900', testId }) => (
  <Card data-testid={testId}><CardContent className="pb-3 pt-4">
    <p className={`text-xl font-bold tabular-nums ${tone}`}>{value ?? 0}</p>
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
  </CardContent></Card>
);

const HospitalAdminPage = () => {
  const { hospitalId } = useParams();
  const [tab, setTab] = useState('overview');

  const { data: me } = useQuery({
    queryKey: ['hospital-me'], queryFn: () => hospitalApi('/hospital/me'), retry: false,
  });
  const { data: hospital, isLoading, error } = useQuery({
    queryKey: ['hospital', hospitalId],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}`), retry: false,
  });

  if (error) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 px-6"
           data-testid="hospital-error">
        <div className="text-center">
          <Lock className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          <p className="text-sm text-slate-600">{error.message}</p>
          <Button asChild size="sm" variant="outline" className="mt-4">
            <Link to="/hospital/login">Sign in as hospital staff</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="hospital-admin-page">
      <header className="border-b bg-white">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Link to="/admin/hospitals" className="text-slate-400 hover:text-slate-700"
                    data-testid="hospital-back"><ArrowLeft className="h-5 w-5" /></Link>
              <div>
                <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900"
                    data-testid="hospital-name">
                  <Stethoscope className="h-5 w-5 text-rose-600" />
                  {isLoading ? <Skeleton className="h-6 w-40" /> : hospital?.name}
                </h1>
                <p className="text-xs text-slate-500" data-testid="hospital-subtitle">
                  {TYPE_LABELS[hospital?.hospital_type] || ''}
                  {hospital?.city ? ` · ${hospital.city}` : ''}
                  {hospital?.parent_name ? ` · under ${hospital.parent_name}` : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {me && (
                <Badge variant="outline" className="text-[10px]" data-testid="hospital-my-role">
                  {me.kind === 'admin' ? 'Super Admin' : ROLE_LABELS[me.role] || me.role}
                </Badge>
              )}
              {hospital?.is_published && (
                <Button asChild size="sm" variant="outline" data-testid="hospital-public-link">
                  <Link to={`/hospital/${hospital.slug}`}>Public page</Link>
                </Button>
              )}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1">
            {TABS.map(([key, labelText, Icon]) => (
              <button key={key} onClick={() => setTab(key)} data-testid={`hospital-tab-${key}`}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${
                        tab === key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                <Icon className="h-3.5 w-3.5" /> {labelText}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 px-4 py-6">
        {tab === 'overview' && <Overview hospitalId={hospitalId} hospital={hospital} />}
        {tab === 'staff' && <StaffTab hospitalId={hospitalId} />}
        {tab === 'patients' && <PatientsTab hospitalId={hospitalId} />}
        {tab === 'appointments' && <AppointmentsTab hospitalId={hospitalId} />}
        {tab === 'pharmacy' && <PharmacyTab hospitalId={hospitalId} />}
        {tab === 'enquiries' && <EnquiriesTab hospitalId={hospitalId} />}
      </main>
    </div>
  );
};

/* ── Overview / reports ──────────────────────────────────────────── */

const Overview = ({ hospitalId, hospital }) => {
  const [days, setDays] = useState(30);
  const from = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
  const { data } = useQuery({
    queryKey: ['hospital-reports', hospitalId, days],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}/reports?date_from=${from}`),
    retry: false,
  });

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-800">Last {days} days</p>
        <div className="flex overflow-hidden rounded-md border border-slate-200 bg-white">
          {[30, 90, 365].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`hospital-range-${d}`}
                    className={`px-2.5 py-1.5 text-[11px] font-medium ${
                      days === d ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
              {d === 365 ? '1 year' : `${d}d`}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Patient visits" value={data?.visits} testId="hospital-stat-visits" />
        <Stat label="Unique patients" value={data?.unique_patients} testId="hospital-stat-patients" />
        <Stat label="Revenue collected" value={data?.revenue} tone="text-emerald-700" testId="hospital-stat-revenue" />
        <Stat label="Outstanding" value={data?.outstanding} tone="text-rose-700" testId="hospital-stat-outstanding" />
        <Stat label="Members" value={data?.member_visits} testId="hospital-stat-members" />
        <Stat label="Community" value={data?.community_visits} testId="hospital-stat-community" />
        <Stat label="Waived" value={data?.waived} tone="text-indigo-700" testId="hospital-stat-waived" />
        <Stat label="Appointments" value={data?.appointments} testId="hospital-stat-appointments" />
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <Card data-testid="hospital-top-treatments"><CardContent className="pt-4">
          <p className="mb-2 text-sm font-semibold text-slate-800">Most common treatments</p>
          {(data?.top_treatments || []).length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">No visits recorded yet.</p>
          ) : data.top_treatments.map((t) => (
            <div key={t.name} className="flex items-center justify-between py-1 text-xs">
              <span className="truncate text-slate-700">{t.name}</span>
              <span className="font-semibold tabular-nums text-slate-500">{t.count}</span>
            </div>
          ))}
        </CardContent></Card>
        <Card data-testid="hospital-staff-activity"><CardContent className="pt-4">
          <p className="mb-2 text-sm font-semibold text-slate-800">Staff activity</p>
          {(data?.staff_activity || []).length === 0 ? (
            <p className="py-6 text-center text-xs text-slate-400">No visits recorded yet.</p>
          ) : data.staff_activity.map((s) => (
            <div key={s.name} className="flex items-center justify-between py-1 text-xs">
              <span className="truncate text-slate-700">
                {s.name} <span className="text-slate-400">{ROLE_LABELS[s.role] || ''}</span>
              </span>
              <span className="font-semibold tabular-nums text-slate-500">{s.visits}</span>
            </div>
          ))}
        </CardContent></Card>
      </div>
      <Card><CardContent className="pt-4 text-xs text-slate-500">
        Patient billing posts to this hospital's own finance account
        {hospital?.parent_name ? ` under ${hospital.parent_name}` : ''} — not into the assembly's
        own books. Waived visits are recorded but never billed.
      </CardContent></Card>
    </>
  );
};

/* ── Staff ───────────────────────────────────────────────────────── */

const StaffTab = ({ hospitalId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const blank = { name: '', email: '', phone: '', role: 'nurse', department: '',
    employment_status: 'active', password: '' };
  const [form, setForm] = useState(blank);

  const { data } = useQuery({
    queryKey: ['hospital-staff', hospitalId],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}/staff`), retry: false,
  });
  const add = useMutation({
    mutationFn: () => hospitalApi(`/hospital/${hospitalId}/staff`,
      { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => {
      toast.success('Staff member added');
      setForm(blank); setOpen(false);
      qc.invalidateQueries({ queryKey: ['hospital-staff', hospitalId] });
    },
    onError: (e) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: (id) => hospitalApi(`/hospital/${hospitalId}/staff/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['hospital-staff', hospitalId] }); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card data-testid="hospital-staff-tab">
      <CardContent className="p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-semibold text-slate-800">
            Staff <span className="text-slate-400">({(data?.items || []).length})</span>
          </p>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="hospital-add-staff-btn">
            <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Add staff
          </Button>
        </div>
        {(data?.items || []).length === 0 ? (
          <p className="py-14 text-center text-sm text-slate-500" data-testid="hospital-staff-empty">
            No staff yet. Add the Hospital Admin first — they can then add everyone else.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.items.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                   data-testid={`hospital-staff-${s.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{s.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {ROLE_LABELS[s.role] || s.role}{s.department ? ` · ${s.department}` : ''}
                    {s.email ? ` · ${s.email}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[10px] ${
                    s.employment_status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-slate-100 text-slate-500'}`}>{s.employment_status}</Badge>
                  {s.has_login && <Badge variant="outline" className="text-[10px]">can sign in</Badge>}
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600"
                          onClick={() => remove.mutate(s.id)} data-testid={`hospital-staff-delete-${s.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="hospital-staff-dialog">
          <DialogHeader>
            <DialogTitle>Add a staff member</DialogTitle>
            <DialogDescription>
              Give them a password and they can sign in at /hospital/login — they land straight
              on this panel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     data-testid="staff-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role</Label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                        data-testid="staff-role-select"
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                  {(data?.roles || []).map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
                  ))}
                </select>
              </div>
              <div><Label>Department</Label>
                <Input value={form.department} data-testid="staff-department-input"
                       onChange={(e) => setForm({ ...form, department: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label>
                <Input type="email" value={form.email} data-testid="staff-email-input"
                       onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div><Label>Phone</Label>
                <Input value={form.phone} data-testid="staff-phone-input"
                       onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div><Label>Password <span className="font-normal text-slate-400">(optional)</span></Label>
              <Input type="password" value={form.password} data-testid="staff-password-input"
                     onChange={(e) => setForm({ ...form, password: e.target.value })}
                     placeholder="At least 8 characters" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => add.mutate()} disabled={add.isPending || !form.name.trim()}
                    data-testid="staff-save-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/* ── Patients + records ──────────────────────────────────────────── */

const PatientsTab = ({ hospitalId }) => {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const blank = { name: '', phone: '', date_of_birth: '', gender: '', address: '',
    next_of_kin_name: '', next_of_kin_phone: '', membership: 'community', church_member_id: '' };
  const [form, setForm] = useState(blank);

  const { data } = useQuery({
    queryKey: ['hospital-patients', hospitalId, q],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}/patients?q=${encodeURIComponent(q)}`),
    retry: false,
  });
  const add = useMutation({
    mutationFn: () => hospitalApi(`/hospital/${hospitalId}/patients`,
      { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: (res) => {
      toast.success(`Registered as ${res.patient_number}`);
      setForm(blank); setOpen(false);
      qc.invalidateQueries({ queryKey: ['hospital-patients', hospitalId] });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card data-testid="hospital-patients-tab">
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="patient-search-input"
                 placeholder="Search by name, phone or patient number" className="max-w-xs" />
          <Button size="sm" onClick={() => setOpen(true)} data-testid="hospital-add-patient-btn">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Register patient
          </Button>
        </div>
        {(data?.items || []).length === 0 ? (
          <p className="py-14 text-center text-sm text-slate-500" data-testid="hospital-patients-empty">
            No patients {q ? 'match that search' : 'registered yet'}.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.items.map((p) => (
              <button key={p.id} onClick={() => setViewing(p)}
                      data-testid={`hospital-patient-${p.id}`}
                      className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50">
                <div>
                  <p className="text-sm font-medium text-slate-800">
                    {p.name} <span className="ml-1 font-mono text-[11px] text-slate-400">{p.patient_number}</span>
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {p.phone || 'no phone'}{p.date_of_birth ? ` · born ${p.date_of_birth}` : ''}
                    {p.next_of_kin_name ? ` · kin ${p.next_of_kin_name}` : ''}
                  </p>
                </div>
                <Badge variant="outline" className={`text-[10px] ${
                  p.membership === 'member' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : ''}`}>
                  {p.membership === 'member' ? 'Church member' : 'Community'}
                </Badge>
              </button>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="hospital-patient-dialog">
          <DialogHeader><DialogTitle>Register a patient</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label>
              <Input value={form.name} data-testid="patient-name-input"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Phone</Label>
                <Input value={form.phone} data-testid="patient-phone-input"
                       onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div><Label>Date of birth</Label>
                <Input type="date" value={form.date_of_birth} data-testid="patient-dob-input"
                       onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} />
              </div>
              <div><Label>Gender</Label>
                <Input value={form.gender} data-testid="patient-gender-input"
                       onChange={(e) => setForm({ ...form, gender: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Next of kin</Label>
                <Input value={form.next_of_kin_name} data-testid="patient-kin-input"
                       onChange={(e) => setForm({ ...form, next_of_kin_name: e.target.value })} />
              </div>
              <div><Label>Kin phone</Label>
                <Input value={form.next_of_kin_phone} data-testid="patient-kin-phone-input"
                       onChange={(e) => setForm({ ...form, next_of_kin_phone: e.target.value })} />
              </div>
            </div>
            <div><Label>Church membership</Label>
              <select value={form.membership} data-testid="patient-membership-select"
                      onChange={(e) => setForm({ ...form, membership: e.target.value })}
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="community">Community patient</option>
                <option value="member">Church member</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => add.mutate()} disabled={add.isPending || !form.name.trim()}
                    data-testid="patient-save-btn">Register</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {viewing && <PatientRecord hospitalId={hospitalId} patient={viewing}
                                 onClose={() => setViewing(null)} />}
    </Card>
  );
};

const PatientRecord = ({ hospitalId, patient, onClose }) => {
  const { data } = useQuery({
    queryKey: ['hospital-record', hospitalId, patient.id],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}/patients/${patient.id}/record`),
    retry: false,
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto"
                     data-testid="hospital-patient-record">
        <DialogHeader>
          <DialogTitle>{patient.name} · {patient.patient_number}</DialogTitle>
          <DialogDescription>
            {patient.membership === 'member' ? 'Church member' : 'Community patient'}
            {patient.phone ? ` · ${patient.phone}` : ''}
          </DialogDescription>
        </DialogHeader>
        {data && !data.clinical_visible && (
          <p className="flex items-start gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600"
             data-testid="record-clinical-locked">
            <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
            {data.clinical_hidden_reason}
          </p>
        )}
        <div className="flex gap-4 text-xs text-slate-600">
          <span>Billed <strong className="tabular-nums">{data?.total_billed ?? 0}</strong></span>
          <span>Paid <strong className="tabular-nums text-emerald-700">{data?.total_paid ?? 0}</strong></span>
        </div>
        <div className="space-y-2">
          {(data?.visits || []).length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500" data-testid="record-no-visits">
              No visits recorded for this patient yet.
            </p>
          ) : data.visits.map((v) => (
            <div key={v.id} className="rounded-lg border border-slate-200 p-3"
                 data-testid={`record-visit-${v.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-800">
                  {v.visit_date}{v.doctor_name ? ` · ${v.doctor_name}` : ''}
                </p>
                <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[v.payment_status] || ''}`}>
                  {v.payment_status} · {v.amount_paid}/{v.amount} {v.currency}
                </Badge>
              </div>
              {v.diagnosis !== undefined && (
                <div className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                  {v.complaint && <p><span className="text-slate-400">Complaint: </span>{v.complaint}</p>}
                  {v.diagnosis && <p><span className="text-slate-400">Diagnosis: </span>{v.diagnosis}</p>}
                  {v.treatment && <p><span className="text-slate-400">Treatment: </span>{v.treatment}</p>}
                  {(v.prescriptions || []).length > 0 && (
                    <p><span className="text-slate-400">Prescribed: </span>{v.prescriptions.join(', ')}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ── Appointments ────────────────────────────────────────────────── */

const AppointmentsTab = ({ hospitalId }) => {
  const qc = useQueryClient();
  const [doctorId, setDoctorId] = useState('');
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ patient_id: '', doctor_id: '', date: today,
    start_time: '09:00', duration_mins: 30, reason: '', status: 'scheduled' });

  const { data: staff } = useQuery({
    queryKey: ['hospital-staff', hospitalId],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}/staff`), retry: false,
  });
  const { data: patients } = useQuery({
    queryKey: ['hospital-patients', hospitalId, ''],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}/patients?q=`), retry: false,
  });
  const { data } = useQuery({
    queryKey: ['hospital-appointments', hospitalId, doctorId],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}/appointments?doctor_id=${doctorId}`),
    retry: false,
  });

  const doctors = (staff?.items || []).filter((s) => s.role === 'doctor');
  const book = useMutation({
    mutationFn: () => hospitalApi(`/hospital/${hospitalId}/appointments`,
      { method: 'POST', body: JSON.stringify({ ...form, duration_mins: Number(form.duration_mins) }) }),
    onSuccess: () => {
      toast.success('Appointment booked'); setOpen(false);
      qc.invalidateQueries({ queryKey: ['hospital-appointments', hospitalId] });
    },
    onError: (e) => toast.error(e.message),
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }) => hospitalApi(
      `/hospital/${hospitalId}/appointments/${id}/status?status=${status}`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hospital-appointments', hospitalId] }),
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card data-testid="hospital-appointments-tab">
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}
                    data-testid="appointment-doctor-filter"
                    className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs">
              <option value="">All doctors</option>
              {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            {Object.entries(data?.counts || {}).map(([s, n]) => (
              <Badge key={s} variant="outline" className={`text-[10px] ${STATUS_TONE[s]}`}
                     data-testid={`appointment-count-${s}`}>{s.replace('_', ' ')} {n}</Badge>
            ))}
          </div>
          <Button size="sm" onClick={() => setOpen(true)} data-testid="hospital-book-btn">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Book appointment
          </Button>
        </div>
        {(data?.items || []).length === 0 ? (
          <p className="py-14 text-center text-sm text-slate-500" data-testid="hospital-appointments-empty">
            Nothing booked for the next fortnight.
          </p>
        ) : Object.entries(data.days).map(([day, rows]) => (
          <div key={day} data-testid={`appointment-day-${day}`}>
            <p className="bg-slate-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {day}
            </p>
            <div className="divide-y divide-slate-100">
              {rows.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5"
                     data-testid={`appointment-${a.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-12 shrink-0 text-sm font-semibold tabular-nums text-slate-700">
                      {a.start_time}
                    </span>
                    <div>
                      <p className="text-sm text-slate-800">{a.patient_name}</p>
                      <p className="text-[11px] text-slate-500">
                        {a.doctor_name}{a.reason ? ` · ${a.reason}` : ''} · {a.duration_mins} mins
                      </p>
                    </div>
                  </div>
                  <select value={a.status} data-testid={`appointment-status-${a.id}`}
                          onChange={(e) => setStatus.mutate({ id: a.id, status: e.target.value })}
                          className={`h-7 rounded-md border px-2 text-[11px] ${STATUS_TONE[a.status]}`}>
                    {(data.statuses || []).map((s) => (
                      <option key={s} value={s}>{s.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="hospital-appointment-dialog">
          <DialogHeader><DialogTitle>Book an appointment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Patient</Label>
              <select value={form.patient_id} data-testid="appointment-patient-select"
                      onChange={(e) => setForm({ ...form, patient_id: e.target.value })}
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="">Choose a patient</option>
                {(patients?.items || []).map((p) => (
                  <option key={p.id} value={p.id}>{p.name} · {p.patient_number}</option>
                ))}
              </select>
            </div>
            <div><Label>Doctor</Label>
              <select value={form.doctor_id} data-testid="appointment-doctor-select"
                      onChange={(e) => setForm({ ...form, doctor_id: e.target.value })}
                      className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
                <option value="">Choose a doctor</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Date</Label>
                <Input type="date" value={form.date} data-testid="appointment-date-input"
                       onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div><Label>Time</Label>
                <Input type="time" value={form.start_time} data-testid="appointment-time-input"
                       onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
              </div>
              <div><Label>Minutes</Label>
                <Input type="number" value={form.duration_mins} data-testid="appointment-duration-input"
                       onChange={(e) => setForm({ ...form, duration_mins: e.target.value })} />
              </div>
            </div>
            <div><Label>Reason</Label>
              <Input value={form.reason} data-testid="appointment-reason-input"
                     onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => book.mutate()}
                    disabled={book.isPending || !form.patient_id || !form.doctor_id}
                    data-testid="appointment-save-btn">Book</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/* ── Pharmacy ────────────────────────────────────────────────────── */

const PharmacyTab = ({ hospitalId }) => {
  const qc = useQueryClient();
  const [medOpen, setMedOpen] = useState(false);
  const [batchFor, setBatchFor] = useState(null);
  const [med, setMed] = useState({ name: '', unit: 'tablet', reorder_level: 20, notes: '' });
  const [batch, setBatch] = useState({ batch_no: '', expiry_date: '', quantity: 10, unit_cost: 0 });

  const { data } = useQuery({
    queryKey: ['hospital-medicines', hospitalId],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}/medicines`), retry: false,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['hospital-medicines', hospitalId] });
  const addMed = useMutation({
    mutationFn: () => hospitalApi(`/hospital/${hospitalId}/medicines`,
      { method: 'POST', body: JSON.stringify({ ...med, reorder_level: Number(med.reorder_level) }) }),
    onSuccess: () => { toast.success('Medicine added'); setMedOpen(false); refresh(); },
    onError: (e) => toast.error(e.message),
  });
  const addBatch = useMutation({
    mutationFn: () => hospitalApi(`/hospital/${hospitalId}/medicines/${batchFor.id}/batches`,
      { method: 'POST', body: JSON.stringify({ ...batch, quantity: Number(batch.quantity),
                                               unit_cost: Number(batch.unit_cost) }) }),
    onSuccess: () => { toast.success('Batch received'); setBatchFor(null); refresh(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      {((data?.reorder_alerts || []).length > 0 || (data?.expiry_alerts || []).length > 0) && (
        <Card className="border-amber-200 bg-amber-50" data-testid="pharmacy-alerts">
          <CardContent className="pt-4 text-xs text-amber-900">
            {(data.reorder_alerts || []).length > 0 && (
              <p className="flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                Reorder now: <strong>{data.reorder_alerts.join(', ')}</strong>
              </p>
            )}
            {(data.expiry_alerts || []).length > 0 && (
              <p className="mt-1 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                Expiring or expired stock: <strong>{data.expiry_alerts.join(', ')}</strong>
              </p>
            )}
          </CardContent>
        </Card>
      )}
      <Card data-testid="hospital-pharmacy-tab">
        <CardContent className="p-0">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="text-sm font-semibold text-slate-800">Medicine stock</p>
            <Button size="sm" onClick={() => setMedOpen(true)} data-testid="pharmacy-add-medicine-btn">
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add medicine
            </Button>
          </div>
          {(data?.items || []).length === 0 ? (
            <p className="py-14 text-center text-sm text-slate-500" data-testid="pharmacy-empty">
              No medicines on the shelf yet.
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.items.map((m) => (
                <div key={m.id} className="px-4 py-3" data-testid={`medicine-${m.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {m.name}
                        <span className="ml-2 text-[11px] font-normal text-slate-400">
                          {m.in_stock} {m.unit}(s) in date
                        </span>
                      </p>
                      <p className="text-[11px] text-slate-500">
                        reorder at {m.reorder_level}
                        {m.expired_quantity > 0 && ` · ${m.expired_quantity} expired`}
                        {m.expiring_soon > 0 && ` · ${m.expiring_soon} batch(es) expiring within 90 days`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {m.needs_reorder && (
                        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]"
                               data-testid={`medicine-reorder-${m.id}`}>reorder</Badge>
                      )}
                      <Button size="sm" variant="outline" data-testid={`medicine-batch-btn-${m.id}`}
                              onClick={() => { setBatch({ batch_no: '', expiry_date: '', quantity: 10, unit_cost: 0 }); setBatchFor(m); }}>
                        Receive batch
                      </Button>
                    </div>
                  </div>
                  {(m.batches || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.batches.map((b) => (
                        <span key={`${b.batch_no}-${b.expiry_date}`}
                              data-testid={`medicine-batch-${m.id}-${b.batch_no}`}
                              className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[10px] text-slate-600">
                          {b.batch_no} · exp {b.expiry_date} · {b.quantity}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>

        <Dialog open={medOpen} onOpenChange={setMedOpen}>
          <DialogContent data-testid="pharmacy-medicine-dialog">
            <DialogHeader><DialogTitle>Add a medicine</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label>
                <Input value={med.name} data-testid="medicine-name-input"
                       onChange={(e) => setMed({ ...med, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Unit</Label>
                  <Input value={med.unit} data-testid="medicine-unit-input"
                         onChange={(e) => setMed({ ...med, unit: e.target.value })} />
                </div>
                <div><Label>Reorder level</Label>
                  <Input type="number" value={med.reorder_level} data-testid="medicine-reorder-input"
                         onChange={(e) => setMed({ ...med, reorder_level: e.target.value })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMedOpen(false)}>Cancel</Button>
              <Button onClick={() => addMed.mutate()} disabled={addMed.isPending || !med.name.trim()}
                      data-testid="medicine-save-btn">Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!batchFor} onOpenChange={(o) => !o && setBatchFor(null)}>
          <DialogContent data-testid="pharmacy-batch-dialog">
            <DialogHeader>
              <DialogTitle>Receive a batch of {batchFor?.name}</DialogTitle>
              <DialogDescription>
                Dispensing always draws from the batch that expires soonest.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Batch number</Label>
                  <Input value={batch.batch_no} data-testid="batch-no-input"
                         onChange={(e) => setBatch({ ...batch, batch_no: e.target.value })} />
                </div>
                <div><Label>Expiry date</Label>
                  <Input type="date" value={batch.expiry_date} data-testid="batch-expiry-input"
                         onChange={(e) => setBatch({ ...batch, expiry_date: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Quantity</Label>
                  <Input type="number" value={batch.quantity} data-testid="batch-quantity-input"
                         onChange={(e) => setBatch({ ...batch, quantity: e.target.value })} />
                </div>
                <div><Label>Unit cost</Label>
                  <Input type="number" value={batch.unit_cost} data-testid="batch-cost-input"
                         onChange={(e) => setBatch({ ...batch, unit_cost: e.target.value })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBatchFor(null)}>Cancel</Button>
              <Button onClick={() => addBatch.mutate()}
                      disabled={addBatch.isPending || !batch.batch_no.trim() || !batch.expiry_date}
                      data-testid="batch-save-btn">Receive</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
    </>
  );
};

/* ── Enquiries from the public page ──────────────────────────────── */

const EnquiriesTab = ({ hospitalId }) => {
  const { data } = useQuery({
    queryKey: ['hospital-enquiries', hospitalId],
    queryFn: () => hospitalApi(`/hospital/${hospitalId}/enquiries`), retry: false,
  });
  return (
    <Card data-testid="hospital-enquiries-tab"><CardContent className="p-0">
      {(data?.items || []).length === 0 ? (
        <p className="py-14 text-center text-sm text-slate-500" data-testid="hospital-enquiries-empty">
          No enquiries from the public page yet.
        </p>
      ) : (
        <div className="divide-y divide-slate-100">
          {data.items.map((e) => (
            <div key={e.id} className="px-4 py-3" data-testid={`enquiry-${e.id}`}>
              <p className="text-sm font-medium text-slate-800">
                {e.name} <span className="font-normal text-slate-400">· {e.contact}</span>
              </p>
              <p className="mt-0.5 text-xs text-slate-600">{e.message}</p>
              <p className="mt-0.5 text-[10px] text-slate-400">{(e.created_at || '').slice(0, 16)}</p>
            </div>
          ))}
        </div>
      )}
    </CardContent></Card>
  );
};

export default HospitalAdminPage;
